import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2, Plus, ShoppingCart, Sparkles, Trash2, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useLang } from '@/context/LangContext';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FX_HINT = { EUR: 1, XOF: 655.957, XAF: 655.957 };

const formatPrice = (eur, currency) => {
    const rate = FX_HINT[currency] || 1;
    const v = eur * rate;
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(v) + ' ' + currency;
};

const packPrice = (p) => Number(p.price_eur || p.monthly_price_eur || 100);

export default function Marketplace() {
    const { t } = useLang();
    const { session, user } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const navigate = useNavigate();
    const [packs, setPacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busySlug, setBusySlug] = useState(null);
    const [currency, setCurrency] = useState('EUR');
    const [cart, setCart] = useState([]);
    const [cartOpen, setCartOpen] = useState(false);
    const [ordering, setOrdering] = useState(false);

    const cartKey = user && currentWorkspace ? `av_cart_${user.id}_${currentWorkspace.id}` : null;

    useEffect(() => {
        if (!cartKey) return;
        try { setCart(JSON.parse(localStorage.getItem(cartKey)) || []); } catch { setCart([]); }
    }, [cartKey]);

    const persistCart = (next) => {
        setCart(next);
        if (cartKey) try { localStorage.setItem(cartKey, JSON.stringify(next)); } catch { /* ignore */ }
    };

    const reload = async () => {
        if (!currentWorkspace || !session) { setLoading(false); return; }
        setLoading(true);
        try {
            const r = await axios.get(`${API}/marketplace/packs`, {
                params: { workspace_id: currentWorkspace.id },
                headers: { Authorization: `Bearer ${session.access_token}` },
            });
            setPacks(r.data.packs || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [currentWorkspace, session]);

    const cartPacks = useMemo(() => cart.map(slug => packs.find(p => p.slug === slug)).filter(Boolean), [cart, packs]);
    const totalEur = cartPacks.reduce((s, p) => s + packPrice(p), 0);

    const addToCart = (slug) => {
        if (cart.includes(slug)) return;
        persistCart([...cart, slug]);
        toast.success('Pack ajouté au panier');
    };

    const removeFromCart = (slug) => persistCart(cart.filter(s => s !== slug));

    const checkout = async () => {
        if (!cart.length) return;
        setOrdering(true);
        try {
            const r = await axios.post(`${API}/billing/stripe/checkout/cart`,
                { workspace_id: currentWorkspace.id, items: cart, origin_url: window.location.origin },
                { headers: { Authorization: `Bearer ${session.access_token}` } });
            if (r.data?.url) {
                persistCart([]);
                window.location.href = r.data.url;
                return;
            }
            toast.error('Session de paiement indisponible');
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setOrdering(false);
    };

    const cancel = async (slug) => {
        if (!currentWorkspace?.is_admin) { toast.error('Admin uniquement'); return; }
        if (!window.confirm("Désactiver ce pack ? Les modèles ne seront plus accessibles.")) return;
        setBusySlug(slug);
        try {
            await axios.post(`${API}/billing/cancel/pack`,
                { workspace_id: currentWorkspace.id, pack_slug: slug },
                { headers: { Authorization: `Bearer ${session.access_token}` } });
            toast.success('Pack désactivé');
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusySlug(null);
    };

    if (!currentWorkspace) return <div className="text-center py-20 text-foreground/60">{t('common.no_workspace')}</div>;

    return (
        <div className="space-y-8" data-testid="marketplace-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="eyebrow mb-2">Marketplace · Packs sectoriels</div>
                    <h1 className="font-heading text-4xl">Modèles documentaires premium</h1>
                    <p className="text-foreground/70 mt-3 max-w-3xl">
                        15 modèles par pack, prêts à l'emploi, alimentés automatiquement avec les données de vos projets.
                        <strong> Paiement unique par pack</strong> (prix affiché en EUR et FCFA) — pas d'abonnement, accès à vie. Vos pilotes
                        gagnent <strong>3 heures</strong> par livrable.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground uppercase tracking-wider">Devise</span>
                    <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                        className="h-9 px-3 rounded-md border border-input bg-background text-sm" data-testid="marketplace-currency">
                        <option value="EUR">EUR €</option>
                        <option value="XOF">XOF (FCFA)</option>
                        <option value="XAF">XAF (FCFA)</option>
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" /></div>
            ) : (
                <div className="grid md:grid-cols-2 gap-6">
                    {packs.map(p => {
                        const inCart = cart.includes(p.slug);
                        return (
                            <div key={p.id} className={`card-soft p-6 relative overflow-hidden ${p.is_subscribed ? 'ring-2 ring-primary/30' : inCart ? 'ring-2 ring-secondary/40' : ''}`}
                                data-testid={`pack-${p.slug}`}>
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-center gap-3">
                                        <div className="h-14 w-14 rounded-xl bg-primary-light text-3xl flex items-center justify-center">
                                            {p.cover_emoji || '📂'}
                                        </div>
                                        <div>
                                            <div className="font-heading text-xl">{p.name}</div>
                                            <div className="text-xs uppercase tracking-wider text-muted-foreground mt-0.5">
                                                {p.templates_count} modèles · {p.sector}
                                            </div>
                                        </div>
                                    </div>
                                    {p.is_subscribed && (
                                        <span className="text-[10px] uppercase tracking-wider bg-primary text-primary-foreground px-2 py-1 rounded-full flex items-center gap-1">
                                            <Sparkles size={10} /> Actif
                                        </span>
                                    )}
                                    {!p.is_subscribed && inCart && (
                                        <span className="text-[10px] uppercase tracking-wider bg-secondary text-secondary-foreground px-2 py-1 rounded-full flex items-center gap-1" data-testid={`pack-incart-badge-${p.slug}`}>
                                            <ShoppingCart size={10} /> Au panier
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-foreground/70 mt-4 leading-relaxed">{p.description}</p>
                                <div className="flex items-end justify-between mt-6 pt-4 border-t border-border">
                                    <div>
                                        <div className="font-heading text-3xl text-primary">{formatPrice(packPrice(p), currency)}</div>
                                        {currency === 'EUR' && (
                                            <div className="text-sm font-medium text-secondary" data-testid={`pack-price-fcfa-${p.slug}`}>
                                                ≈ {Math.round(p.price_xof_display || packPrice(p) * 655.957).toLocaleString('fr-FR')} FCFA
                                            </div>
                                        )}
                                        <div className="text-xs text-muted-foreground">paiement unique · accès à vie</div>
                                    </div>
                                    {p.is_subscribed ? (
                                        <Button variant="ghost" size="sm" onClick={() => cancel(p.slug)}
                                            disabled={busySlug === p.slug} data-testid={`pack-cancel-${p.slug}`}>
                                            {busySlug === p.slug ? <Loader2 size={14} className="animate-spin" /> : <><X size={14} className="mr-1.5" /> Désactiver</>}
                                        </Button>
                                    ) : inCart ? (
                                        <Button variant="outline" onClick={() => removeFromCart(p.slug)} data-testid={`pack-remove-cart-${p.slug}`}>
                                            <Trash2 size={14} className="mr-1.5" /> Retirer
                                        </Button>
                                    ) : (
                                        <Button onClick={() => addToCart(p.slug)}
                                            className="bg-primary hover:bg-primary-hover" data-testid={`pack-add-cart-${p.slug}`}>
                                            <Plus size={14} className="mr-1.5" /> Ajouter au panier
                                        </Button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="card-soft p-6 bg-surface-muted">
                <div className="eyebrow mb-2">Pourquoi un pack sectoriel ?</div>
                <p className="text-sm text-foreground/70 leading-relaxed">
                    Chaque pack contient 15 modèles rédigés par des experts du secteur (énergie, santé, mines, ONG) :
                    APS, APD, contrats AO, attestations, reporting bailleurs. Les variables sont automatiquement remplies
                    depuis les données de vos projets. <strong>2 modèles génériques sont offerts</strong> par espace pour
                    tester la valeur avant achat. Paiement unique par pack, accès permanent.
                </p>
            </div>

            {/* Panier flottant — visible uniquement quand il contient des articles */}
            {cart.length > 0 && (
                <button onClick={() => setCartOpen(true)}
                    className="fixed bottom-6 right-6 z-40 h-14 pl-5 pr-6 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary-hover transition-colors flex items-center gap-3"
                    data-testid="floating-cart-button">
                    <span className="relative">
                        <ShoppingCart size={20} strokeWidth={1.8} />
                        <span className="absolute -top-2.5 -right-2.5 h-5 w-5 rounded-full bg-secondary text-secondary-foreground text-[11px] font-bold flex items-center justify-center"
                            data-testid="cart-badge-count">{cart.length}</span>
                    </span>
                    <span className="font-medium text-sm">{formatPrice(totalEur, currency)}</span>
                </button>
            )}

            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
                <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
                    <SheetHeader>
                        <SheetTitle className="font-heading text-2xl flex items-center gap-2">
                            <ShoppingCart size={20} strokeWidth={1.8} /> Mon panier
                        </SheetTitle>
                        <SheetDescription>
                            {cart.length} article{cart.length > 1 ? 's' : ''} · paiement unique, accès à vie
                        </SheetDescription>
                    </SheetHeader>
                    <div className="flex-1 overflow-y-auto py-4 space-y-3">
                        {cartPacks.map(p => (
                            <div key={p.slug} className="flex items-center gap-3 p-3 rounded-lg border border-border" data-testid={`cart-item-${p.slug}`}>
                                <div className="h-10 w-10 rounded-lg bg-primary-light text-xl flex items-center justify-center shrink-0">
                                    {p.cover_emoji || '📂'}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium text-sm truncate">{p.name}</div>
                                    <div className="text-xs text-muted-foreground">{formatPrice(packPrice(p), currency)}</div>
                                </div>
                                <Button variant="ghost" size="icon" className="shrink-0 text-muted-foreground hover:text-destructive"
                                    onClick={() => removeFromCart(p.slug)} data-testid={`cart-remove-${p.slug}`}>
                                    <Trash2 size={15} />
                                </Button>
                            </div>
                        ))}
                        {cartPacks.length === 0 && (
                            <p className="text-sm text-foreground/60 text-center py-10">Votre panier est vide.</p>
                        )}
                    </div>
                    <div className="border-t border-border pt-4 space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Total ({currency})</span>
                            <span className="font-heading text-2xl text-primary" data-testid="cart-total">{formatPrice(totalEur, currency)}</span>
                        </div>
                        <Button className="w-full bg-primary hover:bg-primary-hover h-12 text-base"
                            onClick={checkout} disabled={ordering || cart.length === 0} data-testid="cart-checkout-button">
                            {ordering ? <Loader2 size={16} className="animate-spin mr-2" /> : <Check size={16} className="mr-2" />}
                            Passer commande
                        </Button>
                        <p className="text-[11px] text-muted-foreground text-center">
                            Paiement sécurisé par Stripe — les packs sont activés dès confirmation du paiement.
                        </p>
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
}
